import type { Partner } from "@expert/shared-types";

import { immerable } from "immer";

import type { CaseDetails } from "@/shared-types";

import type { AgentActivity, ChannelKind, Locale, TaskStatus, TaskStatusReason } from "./types";

import { DEFAULT_MAX_SUMMARY_GENERATION_DURATION } from "./config";

export abstract class Task {
    [immerable] = true;
    public status: TaskStatus = "pending";

    public readonly autoAccept: boolean = true;

    public readonly wrappingDuration: number = DEFAULT_MAX_SUMMARY_GENERATION_DURATION;

    abstract get assignedAt(): Date | undefined;

    abstract get partner(): Partner;

    public statusReason: TaskStatusReason | undefined;

    public statusUpdatedAt: Date | undefined;

    abstract get caseDetails(): CaseDetails | undefined;

    abstract updateCaseDetails(caseDetailsUpdates: Partial<CaseDetails>): Promise<void>;

    abstract get sessionId(): string | undefined;

    // TODO: Nasty way to have session from the very beginning
    abstract get sessionIdFallback(): string;

    public abstract get hasCustomer(): boolean;

    public abstract get previousTaskId(): string | undefined;

    public abstract get locale(): Locale | undefined;

    public abstract get analyticsData(): Record<string, string>;

    /**
     * This function tells you whether the agent is busy based on the task type and their current status
     * @param agentActivity The agents current activity
     */
    public abstract isAgentBusy(agentActivity: AgentActivity): boolean;

    public readonly onAcceptStatus?: AgentActivity;

    public onAccepted?(): void | Promise<void>;

    /**
     * This function extracts from task simple details for logging.
     * It uses explicit properties name so we can destructure the object in the root level of the log record
     * @returns Task details for logging
     */
    public toLog(): Record<string, unknown> {
        return {
            taskId: this.id,
            taskName: this.name,
            sessionId: this.sessionId,
            taskStatus: this.status,
            taskAssignedAt: this.assignedAt,
            taskPartner: this.partner,
        };
    }

    public readonly type: ChannelKind;
    public readonly id: string;
    public readonly name: string;
    public readonly dateCreated: Date;

    /**
     * @param mdn - The contact (chat || voice) MDN
     * @param previousTaskId - The taskId for the task preceding this one.
     * For instance, an outbound call started from an existing task will contain a previousTaskId
     * Or for transfers in chat
     */
    constructor(type: ChannelKind, id: string, name: string, dateCreated: Date) {
        this.type = type;
        this.id = id;
        this.name = name;
        this.dateCreated = dateCreated;
    }
}
